import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { whatsAppAPI, metaAssetsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Select } from '../components/ui'

const TemplatesPage = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingAssets, setLoadingAssets] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedAssetId, setSelectedAssetId] = useState('')
  const [deletingId, setDeletingId] = useState(null)
  const [expandedTemplate, setExpandedTemplate] = useState(null)

  // Load assets on mount
  useEffect(() => {
    loadAssets()
  }, [])

  // Load templates when asset filter changes
  useEffect(() => {
    if (selectedAssetId) {
      loadTemplates()
    } else {
      setTemplates([])
    }
  }, [selectedAssetId])

  const loadAssets = async () => {
    try {
      setLoadingAssets(true)
      setError(null)
      const response = await metaAssetsAPI.getAssets({ asset_type: 'whatsapp_account' })

      if (response.data.success) {
        const assetList = response.data.data?.data || response.data.data || []
        setAssets(assetList)
        
        // Auto-select first asset if available
        if (assetList.length > 0 && !selectedAssetId) {
          setSelectedAssetId(assetList[0].id.toString())
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assets')
    } finally {
      setLoadingAssets(false)
    }
  }

  const loadTemplates = async () => {
    if (!selectedAssetId) return

    try {
      setLoading(true)
      setError(null)
      const response = await whatsAppAPI.getTemplates(selectedAssetId)

      if (response.data.success) {
        const templateList = Array.isArray(response.data.data)
          ? response.data.data
          : []
        setTemplates(templateList)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    if (!selectedAssetId) {
      setError('Please select an asset first')
      return
    }

    try {
      setSyncing(true)
      setError(null)
      setSuccess(null)

      const response = await whatsAppAPI.syncTemplates(selectedAssetId)

      if (response.data.success) {
        setSuccess('Templates synced successfully')
        await loadTemplates()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync templates')
      setTimeout(() => setError(null), 5000)
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingId(templateId)
      setError(null)
      setSuccess(null)

      const response = await whatsAppAPI.deleteTemplate(templateId)

      if (response.data.success) {
        setSuccess('Template deleted successfully')
        await loadTemplates()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete template')
      setTimeout(() => setError(null), 5000)
    } finally {
      setDeletingId(null)
    }
  }


  const assetOptions = assets.map(asset => ({
    value: asset.id.toString(),
    label: `${asset.asset_name} (${asset.asset_type})`
  }))

  const selectedAsset = assets.find(a => a.id.toString() === selectedAssetId)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">WhatsApp Templates</h1>
        <p className="text-gray-600 mt-1">
          Manage your WhatsApp message templates. Create new templates, sync from Meta, or delete existing ones.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="md:w-1/3">
              <Select
                id="asset-filter"
                label="Filter by Asset"
                options={assetOptions}
                placeholder={loadingAssets ? "Loading assets..." : "Select an asset"}
                value={selectedAssetId}
                onChange={(e) => setSelectedAssetId(e.target.value)}
                disabled={loadingAssets || assets.length === 0}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="secondary"
                onClick={handleSync}
                disabled={syncing || !selectedAssetId}
                loading={syncing}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {syncing ? 'Syncing...' : 'Sync Templates'}
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/templates/create')}
                disabled={!selectedAssetId}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Template
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : !selectedAssetId ? (
            <div className="text-center py-12 text-gray-500">
              <p>Please select an asset to view templates</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium mb-2">No templates found</p>
              <p className="text-sm mb-4">
                {selectedAsset
                  ? `No templates found for ${selectedAsset.asset_name}. Create a new template or sync from Meta to get started.`
                  : 'Create a new template or sync from Meta to get started'}
              </p>
              <div className="flex items-center justify-center space-x-2">
                <Button variant="primary" onClick={() => navigate('/templates/create')} disabled={!selectedAssetId}>
                  Create Template
                </Button>
                <Button variant="secondary" onClick={handleSync} disabled={syncing || !selectedAssetId}>
                  Sync Templates
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template, index) => (
                    <>
                      <tr key={template.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {template.name || template.template_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {template.status ? (
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              template.status === 'APPROVED' 
                                ? 'bg-green-100 text-green-800' 
                                : template.status === 'PENDING'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.status}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {template.category || <span className="text-gray-400 italic">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {template.language || <span className="text-gray-400 italic">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {template.created_at ? new Date(template.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                              className="text-blue-600 hover:text-blue-900 font-medium transition-colors"
                            >
                              {expandedTemplate === template.id ? 'Hide' : 'View'}
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={() => handleDelete(template.id)}
                              disabled={deletingId === template.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 font-medium transition-colors"
                            >
                              {deletingId === template.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedTemplate === template.id && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Meta Template ID:</span>
                                  <p className="text-sm text-gray-900">{template.meta_template_id || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Parameter Format:</span>
                                  <p className="text-sm text-gray-900">{template.parameter_format || '-'}</p>
                                </div>
                                {template.previous_category && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500">Previous Category:</span>
                                    <p className="text-sm text-gray-900">{template.previous_category}</p>
                                  </div>
                                )}
                              </div>
                              {template.components && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Components:</span>
                                  <pre className="mt-1 p-3 bg-white border border-gray-200 rounded text-xs overflow-x-auto">
                                    {JSON.stringify(template.components, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

    </div>
  )
}

export default TemplatesPage
